/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {
  createMsalInstance,
  initializeMsal as kitInitializeMsal,
  makeLoginRequest,
  makeTokenProvider,
  type AuthMode,
  type MsalAuthConfig,
} from '@vaas/react-ui-kit/auth';

import { config, isAuthConfigured } from '@/lib/config';

/** Entra sign-in when configured; mock session for local dev without an IdP. */
export const authMode: AuthMode = isAuthConfigured() ? 'msal' : 'mock';

const authConfig: MsalAuthConfig = {
  clientId: config.auth.clientId,
  authority: config.auth.authority,
  redirectUri: config.auth.redirectUri,
  postLogoutRedirectUri: config.auth.postLogoutRedirectUri,
  apiScopes: config.auth.apiScopes,
  debug: import.meta.env.DEV,
};

export const msalInstance = createMsalInstance(authConfig);

export const loginRequest = makeLoginRequest(authConfig);

export const tokenProvider = makeTokenProvider(msalInstance, config.auth.apiScopes);

// Boot record of the effective auth config so an env/registration mismatch shows up
// in the console instead of a silent no-network screen.
console.info(
  `[auth] mode=${authMode} redirectUri=${config.auth.redirectUri} ` +
    `clientId=${config.auth.clientId || '(none)'} authority=${config.auth.authority || '(none)'}`,
);

/** No-op in mock mode so dev boots without an IdP. */
export async function initializeMsal(): Promise<void> {
  if (authMode !== 'msal') return;
  try {
    await kitInitializeMsal(msalInstance);
  } catch (err) {
    // Surface Entra failures (e.g. AADSTS9002326 when the redirect URI is registered as
    // "Web" instead of "Single-page application") rather than a silent stuck screen.
    console.error('[auth] MSAL initialize/handleRedirect failed:', err);
  }
}
